import {
  Box,
  Chip,
  createStyles,
  Grid,
  IconButton,
  makeStyles,
  MenuItem,
  Select,
  FormHelperText,
  InputLabel,
} from '@material-ui/core';
import React, { useState, useEffect, ChangeEvent } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { removeSpecialChars } from '../../../../../services/mask';
import ButtonComponent from '../../../../atoms/button';
import Input from '../../../../atoms/input';
import Typography from '../../../../atoms/typography';
import { vehicleSchema, SingleVehicleForm } from './schema';
import colors from '../../../../../assets/styles/colors';
import commons from '../../../../../assets/styles/commons';
import { ReactComponent as InfoIcon } from '../../../../../assets/icons/icon-info.svg';
import { useHistory } from 'react-router-dom';
import ModalCustom from '../../../../atoms/modalCustom';
import { ReactComponent as IconDelete } from '../../../../../assets/icons/icon-cross-circle-white.svg';
import ImageUploadField from '../../../../atoms/imageUploadFile';
import useBulkCreatePrivateVehicles from '../../../../../hooks/vehicle/useBulkCreatePrivateVehicles';
import type { IVehicleData } from '../../../../../hooks/vehicle/useBulkCreatePrivateVehicles';
import useGetVehicleTypes from '../../../../../hooks/vehicleType/useGetVehicleTypes';

interface FormErrors {
  [key: string]: string | FormErrors | FormErrors[] | undefined;
  vehicles?: FormErrors[];
}

const useStyles = makeStyles(() =>
  createStyles({
    chip: {
      backgroundColor: colors.neutral.superLightGray,
      color: colors.secondary.intenseNavy,
      border: '1px solid #8F9BB3',
      borderRadius: '8px',
      padding: 5,
      fontFamily: commons.fontFamily,
      fontSize: 14,
      flex: 1,
      display: 'flex',
      justifyContent: 'flex-start',
      fontVariant: 'lining-nums',
    },
    selectLabel: {
      marginBottom: '4px',
      color: colors.neutral.darkGray,
    },
    crlvUploadField: {
      marginTop: '16px',
    },
    formActions: {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: '32px',
      gap: '16px',
      flexWrap: 'wrap',
    },
  }),
);

const CreateVehiclesForm: React.FC<any> = ({ onClick }: any) => {
  const classes = useStyles();
  const history = useHistory();
  const { execute: getVehiclesTypesService } = useGetVehicleTypes()
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [vehiclesTypes, setVehiclesTypes] = useState<any>([]);
  const [vehicles, setVehicles] = useState<SingleVehicleForm[]>([
    {
      id: uuidv4(),
      model: '',
      year: '',
      licensePlate: '',
      payloadCapacity: 0,
      vehicleType: '',
      dimensions: {
        weight: 0,
        length: 0,
        width: 0,
      },
      crlv: {
        documentNumber: '',
        expirationDate: '',
        isDigital: false,
        imageUrlFront: null,
        imageUrlBack: null,
      },
      images: {
        imageUrlBack: null,
        imageUrlFront: null,
        imageUrlLeft: null,
        imageUrlRight: null,
      },
    },
  ]);

  const [formErrors, setFormErrors] = useState<FormErrors>({});

  // useEffect(() => {
  //   const handler = setTimeout(() => {
  //     validateForm();
  //   }, 500);

  //   return () => {
  //     clearTimeout(handler);
  //   };
  // }, [vehicles]);

  useEffect(() => {
    getVehiclesTypes()
  }, [])

  const handleInputChange = (
    index: number,
    field: keyof SingleVehicleForm,
    value: any,
  ) => {
    const updatedVehicles = [...vehicles];
    if (field === 'year' || field === 'payloadCapacity') {
      value = value === '' ? 0 : parseInt(value, 10) || 0;
    }
    (updatedVehicles[index] as any)[field] = value;
    setVehicles(updatedVehicles);
  };

  const handleNestedInputChange = (
    index: number,
    parentField: 'crlv' | 'dimensions' | 'images',
    nestedField: string,
    value: any,
  ) => {
    const updatedVehicles = [...vehicles];
    const updatedNestedObject = {
      ...(updatedVehicles[index][parentField] as any),
      [nestedField]: value,
    };
    if (parentField === 'dimensions') {
      if (
        nestedField === 'weight' ||
        nestedField === 'length' ||
        nestedField === 'width'
      ) {
        updatedNestedObject[nestedField] =
          value === '' ? 0 : (value.includes(',') ? value.replace(',', '.') : value) || 0;
      }
    }
    (updatedVehicles[index] as any)[parentField] = updatedNestedObject;
    setVehicles(updatedVehicles);
  };

  const addVehicleToForm = () => {
    setVehicles([
      ...vehicles,
      {
        id: uuidv4(),
        model: '',
        year: '',
        licensePlate: '',
        payloadCapacity: 0,
        vehicleType: '',
        dimensions: {
          weight: 0,
          length: 0,
          width: 0,
        },
        crlv: {
          documentNumber: '',
          expirationDate: '',
          isDigital: false,
          imageUrlFront: null,
          imageUrlBack: null,
        },
        images: {
          imageUrlBack: null,
          imageUrlFront: null,
          imageUrlLeft: null,
          imageUrlRight: null,
        },
      },
    ]);
    setFormErrors(prevErrors => {
      const newErrors = { ...prevErrors };
      if (Array.isArray(newErrors.vehicles)) {
        newErrors.vehicles.push({});
      } else {
        newErrors.vehicles = [{}];
      }
      return newErrors;
    });
  };

  const deleteVehicle = (indexToRemove: number) => {
    setVehicles(vehicles.filter((_, index) => index !== indexToRemove));
    setFormErrors(prevErrors => {
      const newErrors = { ...prevErrors };
      if (Array.isArray(newErrors.vehicles)) {
        newErrors.vehicles.splice(indexToRemove, 1);
      }
      return newErrors;
    });
  };

  const validateForm = async () => {
    try {
      await vehicleSchema.validate({ vehicles }, { abortEarly: false });
      setFormErrors({});
      return true;
    } catch (err: any) {
      const newErrors: FormErrors = {};
      if (err.inner) {
        err.inner.forEach((error: any) => {
          const pathParts = error.path.split('.');
          const ensurePath = (obj: FormErrors, parts: string[], value: any) => {
            let current = obj;
            for (let i = 0; i < parts.length; i++) {
              const part = parts[i];
              const isLast = i === parts.length - 1;
              const arrayMatch = part.match(/(\w+)\[(\d+)\]/);

              if (arrayMatch) {
                const arrayName = arrayMatch[1];
                const index = parseInt(arrayMatch[2], 10);
                if (!current[arrayName]) {
                  current[arrayName] = [];
                }
                if (!(current[arrayName] as FormErrors[])[index]) {
                  (current[arrayName] as FormErrors[])[index] = {};
                }
                current = (current[arrayName] as FormErrors[])[index];
              } else if (!isLast) {
                if (!current[part]) {
                  current[part] = {};
                }
                current = current[part] as FormErrors;
              } else {
                current[part] = value;
              }
            }
          };
          ensurePath(newErrors, pathParts, error.message);
        });
      }
      setFormErrors(newErrors);
      return false;
    }
  };

  const { execute, loading } = useBulkCreatePrivateVehicles();

  const registerVehicles = async (e: React.FormEvent) => {
    e.preventDefault();
    const isValid = await validateForm();

    if (!isValid) {
      console.log('Formulário inválido, verifique os erros:', formErrors);
      toast.error('Por favor, preencha todos os campos obrigatórios corretamente.');
      return;
    }

    console.log('Dados do formulário de veículo (enviado):', vehicles);

    try {
      await execute(
        vehicles.map(vehicle => ({
          model: vehicle.model,
          year: vehicle.year.toString().substring(0, 4),
          licensePlate: vehicle.licensePlate,
          payloadCapacity: Number(vehicle.payloadCapacity) || 0,
          vehicleType: vehicle.vehicleType as IVehicleData['vehicleType'],
          dimensions: {
            weight: Number(vehicle.dimensions.weight) || 0,
            length: Number(vehicle.dimensions.length) || 0,
            width: Number(vehicle.dimensions.width) || 0,
          },
          crlv: {
            documentNumber: vehicle.crlv.documentNumber,
            expirationDate: vehicle.crlv.expirationDate,
            isDigital: vehicle.crlv.isDigital,
            imageUrlFront: vehicle.crlv.imageUrlFront,
            imageUrlBack: vehicle.crlv.imageUrlBack,
          },
          images: {
            imageUrlFront: vehicle.images.imageUrlFront,
            imageUrlBack: vehicle.images.imageUrlBack,
            imageUrlLeft: vehicle.images.imageUrlLeft,
            imageUrlRight: vehicle.images.imageUrlRight,
          },
        })),
        () => {
          toast.success('Veículo(s) criado(s) com sucesso! 🎉');
          history.goBack();
        },
      );
    } catch (error: any) { // Capture o erro para poder pegar a mensagem
      console.error('Erro ao registrar veículos:', error);
      const errorMessage = error.response?.data?.message || 'Ocorreu um erro ao registrar o(s) veículo(s).';
      toast.error(`Erro: ${errorMessage}`);
    }
  };

  const getVehiclesTypes = async () => {

    try {

      const response: any = await getVehiclesTypesService(1, '', 1000)

      const mappedvehiclesTypes = response.rows.map((vehicleType: any) => ({
        label: vehicleType.vehicleType,
        value: vehicleType.vehicleTypeId,
      }));

      setVehiclesTypes(mappedvehiclesTypes)

    } catch (error) {
      console.error('Erro buscar tipos de veiculos:', error);
      toast.error('Erro ao carregar tipos de veículos.');
    }
  };

  const cancelOperation = () => {
    history.goBack();
  };

  const isDirty = React.useMemo(() => {
    if (Object.keys(formErrors).length > 0) return true;

    if (vehicles.length > 1) return true;

    if (vehicles.length === 1) {

      const defaultVehicle: SingleVehicleForm = {
        id: '',
        model: '',
        year: new Date().getFullYear().toString(),
        licensePlate: '',
        payloadCapacity: 0,
        vehicleType: '',
        dimensions: { weight: 0, length: 0, width: 0 },
        crlv: {
          documentNumber: '',
          expirationDate: '',
          isDigital: false,
          imageUrlFront: null,
          imageUrlBack: null,
        },
        images: {
          imageUrlBack: null,
          imageUrlFront: null,
          imageUrlLeft: null,
          imageUrlRight: null,
        },
      };

      const currentVehicle = vehicles[0];
      let hasChanges = false;

      const compareValues = (val1: any, val2: any) => {
        if (val1 instanceof File && val2 instanceof File) {
          return (
            val1 !== val2 || val1.name !== val2.name || val1.size !== val2.size
          );
        }
        if (
          (val1 instanceof File && val2 === null) ||
          (val1 === null && val2 instanceof File)
        ) {
          return true;
        }
        if (
          (val1 instanceof File && typeof val2 === 'string') ||
          (typeof val1 === 'string' && val2 instanceof File)
        ) {
          return true;
        }
        return val1 !== val2;
      };

      for (const key in currentVehicle) {
        if (key === 'id') continue;

        const typedKey = key as keyof SingleVehicleForm;
        const defaultValue = defaultVehicle[typedKey];
        const currentValue = currentVehicle[typedKey];

        if (
          typeof currentValue === 'object' &&
          currentValue !== null &&
          typeof defaultValue === 'object' &&
          defaultValue !== null
        ) {
          const nestedCurrent = currentValue as any;
          const nestedDefault = defaultValue as any;

          for (const nestedKey in nestedCurrent) {
            if (
              compareValues(nestedCurrent[nestedKey], nestedDefault[nestedKey])
            ) {
              hasChanges = true;
              break;
            }
          }
        } else if (compareValues(currentValue, defaultValue)) {
          hasChanges = true;
          break;
        }
        if (hasChanges) break;
      }
      return hasChanges;
    }
    return false;
  }, [vehicles, formErrors]);

  const decideToCancelOperation = () => {
    if (!isDirty) cancelOperation();
    else setOpenCancelModal(true);
  };

  const isFormValid =
    Object.keys(formErrors).length === 0 && vehicles.length > 0;

  return (
    <Box style={{ display: 'flex', gap: 20, flexDirection: 'column' }}>
      <Typography fontWeight="700" color="secondary">
        Adicionar veículos
      </Typography>
      {!isFormValid && <Chip
        label="É necessário adicionar as informações completas dos veículos."
        icon={<InfoIcon />}
        className={classes.chip}
      />}

      <Box>
        <form onSubmit={registerVehicles}>
          {vehicles.map((vehicle, index) => {
            const vehicleErrors = (
              formErrors.vehicles && Array.isArray(formErrors.vehicles)
                ? formErrors.vehicles[index]
                : {}
            ) as FormErrors;

            const crlvErrors = vehicleErrors.crlv as FormErrors;
            const dimensionsErrors = vehicleErrors.dimensions as FormErrors;
            const imagesErrors = vehicleErrors.images as FormErrors;

            return (
              <Grid
                container
                spacing={2}
                key={vehicle.id}
                style={{
                  marginBottom: '20px',
                  border: '1px solid #ccc',
                  padding: '10px',
                  borderRadius: '8px',
                  position: 'relative',
                }}
              >
                {vehicles.length > 1 && (
                  <IconButton
                    aria-label="remover veículo"
                    onClick={() => deleteVehicle(index)}
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      backgroundColor: colors.error.red,
                      color: 'white',
                      padding: 5,
                      borderRadius: '0 8px 0 8px',
                      zIndex: 1,
                    }}
                  >
                    <IconDelete style={{ fontSize: '20px' }} />
                  </IconButton>
                )}

                <Grid item xs={12}>
                  <Typography
                    fontWeight="bold"
                    style={{ marginBottom: '10px' }}
                  >
                    Informações gerais
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Input
                    size="small"
                    label="Modelo"
                    placeholder="Caminhão"
                    value={vehicle.model}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleInputChange(index, 'model', e.target.value)
                    }
                    onBlur={validateForm}
                    error={!!vehicleErrors.model}
                    helperText={vehicleErrors.model as string}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Input
                    size="small"
                    label="Ano"
                    placeholder="2025"
                    value={vehicle.year || ''}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleInputChange(index, 'year', e.target.value)
                    }
                    onBlur={validateForm}
                    error={!!vehicleErrors.year}
                    helperText={vehicleErrors.year as string}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Input
                    size="small"
                    label="Placa"
                    placeholder="AAA1234"
                    value={vehicle.licensePlate}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleInputChange(
                        index,
                        'licensePlate',
                        removeSpecialChars(e.target.value).toUpperCase(),
                      )
                    }
                    onBlur={validateForm}
                    error={!!vehicleErrors.licensePlate}
                    helperText={vehicleErrors.licensePlate as string}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Input
                    size="small"
                    label="Capacidade de carga (kg)"
                    placeholder="800"
                    value={vehicle.payloadCapacity || ''}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleInputChange(
                        index,
                        'payloadCapacity',
                        e.target.value,
                      )
                    }
                    onBlur={validateForm}
                    error={!!vehicleErrors.payloadCapacity}
                    helperText={vehicleErrors.payloadCapacity as string}
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Input
                    size="small"
                    label="Peso (kg)"
                    placeholder="215.77"
                    value={vehicle.dimensions.weight || ''}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleNestedInputChange(
                        index,
                        'dimensions',
                        'weight',
                        e.target.value,
                      )
                    }
                    onBlur={validateForm}
                    error={!!dimensionsErrors?.weight}
                    helperText={dimensionsErrors?.weight as string}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Input
                    size="small"
                    label="Comprimento do baú (m)"
                    placeholder="1.32"
                    value={vehicle.dimensions.length || ''}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleNestedInputChange(
                        index,
                        'dimensions',
                        'length',
                        e.target.value,
                      )
                    }
                    onBlur={validateForm}
                    error={!!dimensionsErrors?.length}
                    helperText={dimensionsErrors?.length as string}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Input
                    size="small"
                    label="Largura do baú (m)"
                    placeholder="0.70"
                    value={vehicle.dimensions.width || ''}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleNestedInputChange(
                        index,
                        'dimensions',
                        'width',
                        e.target.value,
                      )
                    }
                    onBlur={validateForm}
                    error={!!dimensionsErrors?.width}
                    helperText={dimensionsErrors?.width as string}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <InputLabel
                    id={`vehicle-type-label-${index}`}
                    className={classes.selectLabel}
                  >
                    Tipo de veículo
                  </InputLabel>
                  <Select
                    labelId={`vehicle-type-label-${index}`}
                    id={`vehicle-type-select-${index}`}
                    value={vehicle.vehicleType}
                    onChange={e =>
                      handleInputChange(
                        index,
                        'vehicleType',
                        e.target.value as SingleVehicleForm['vehicleType'],
                      )
                    }
                    onBlur={validateForm}
                    displayEmpty
                    fullWidth
                    variant="outlined"
                    error={!!vehicleErrors.vehicleType}
                  >
                    <MenuItem value="" >Selecione o Tipo</MenuItem>
                    {vehiclesTypes && vehiclesTypes.map((vehicleType: any) => <MenuItem key={vehicleType.value} value={vehicleType.value}>{vehicleType.label}</MenuItem>)}
                  </Select>
                  {vehicleErrors.vehicleType && (
                    <FormHelperText error>
                      {vehicleErrors.vehicleType as string}
                    </FormHelperText>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    fontWeight="bold"
                    style={{ marginTop: '20px', marginBottom: '10px' }}
                  >
                    CRLV
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={12}>
                  <Box display="flex" alignItems="center">
                    <Typography style={{ marginRight: '1rem' }}>
                      Tipo de CRLV:
                    </Typography>
                    <label style={{ marginRight: '1rem', display: 'flex', alignItems: 'normal', marginBottom: '0' }}>
                      <input
                        style={{ marginRight: '4px', accentColor: 'gray' }}
                        type="radio"
                        checked={vehicle.crlv.isDigital === false}
                        onChange={() => {
                          handleNestedInputChange(
                            index,
                            'crlv',
                            'isDigital',
                            false,
                          );
                          validateForm();
                        }}
                      />
                      CRLV Física
                    </label>
                    <label style={{ display: 'flex', alignItems: 'normal', marginBottom: '0' }}>
                      <input
                        style={{ marginRight: '4px', accentColor: 'gray' }}
                        type="radio"
                        checked={vehicle.crlv.isDigital === true}
                        onChange={() => {
                          handleNestedInputChange(
                            index,
                            'crlv',
                            'isDigital',
                            true,
                          );
                          validateForm();
                        }}
                      />
                      CRLV Digital
                    </label>
                  </Box>
                  {crlvErrors?.isDigital && (
                    <FormHelperText error>
                      {crlvErrors.isDigital as string}
                    </FormHelperText>
                  )}
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Input
                    size="small"
                    label="Documento CRLV"
                    placeholder="00000000000"
                    value={vehicle.crlv.documentNumber}
                    onBlur={validateForm}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleNestedInputChange(
                        index,
                        'crlv',
                        'documentNumber',
                        removeSpecialChars(e.target.value),
                      )
                    }
                    error={!!crlvErrors?.documentNumber}
                    helperText={crlvErrors?.documentNumber as string}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Input
                    size="small"
                    label="Vencimento CRLV (DD/MM/AAAA)"
                    placeholder="DD/MM/AAAA"
                    onBlur={validateForm}
                    value={vehicle.crlv.expirationDate}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleNestedInputChange(
                        index,
                        'crlv',
                        'expirationDate',
                        e.target.value,
                      )
                    }
                    type='date'
                    error={!!crlvErrors?.expirationDate}
                    helperText={crlvErrors?.expirationDate as string}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    fontWeight="bold"
                    style={{ marginTop: '1rem', marginBottom: '0.5rem' }}
                  >
                    Anexar CRLV
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <ImageUploadField
                        label="Frente do CRLV"
                        onChange={file => {
                          handleNestedInputChange(
                            index,
                            'crlv',
                            'imageUrlFront',
                            file,
                          );
                          validateForm();
                        }}
                        currentValue={vehicle.crlv.imageUrlFront}
                        error={!!crlvErrors?.imageUrlFront}
                        helperText={crlvErrors?.imageUrlFront as string}
                        acceptedFileTypes="image/jpeg,image/png,application/pdf"
                        placeholder="Anexe a frente aqui..."
                      />
                    </Grid>
                    {!vehicle.crlv.isDigital && <Grid item xs={12} sm={6}>
                      <ImageUploadField
                        label="Verso do CRLV"
                        onChange={file => {
                          handleNestedInputChange(
                            index,
                            'crlv',
                            'imageUrlBack',
                            file,
                          );
                          validateForm();
                        }}
                        currentValue={vehicle.crlv.imageUrlBack}
                        error={!!crlvErrors?.imageUrlBack}
                        helperText={crlvErrors?.imageUrlBack as string}
                        acceptedFileTypes="image/jpeg,image/png,application/pdf"
                        placeholder="Anexe o verso aqui..."
                      />
                    </Grid>
                    }
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    fontWeight="bold"
                    style={{ marginTop: '20px', marginBottom: '10px' }}
                  >
                    Imagens do veículo (Opcional)
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ImageUploadField
                    label="Imagem Frontal do Veículo"
                    onChange={file =>
                      handleNestedInputChange(
                        index,
                        'images',
                        'imageUrlFront',
                        file,
                      )
                    }
                    currentValue={vehicle.images.imageUrlFront}
                    error={!!imagesErrors?.imageUrlFront}
                    helperText={imagesErrors?.imageUrlFront as string}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ImageUploadField
                    label="Imagem Traseira do Veículo"
                    onChange={file =>
                      handleNestedInputChange(
                        index,
                        'images',
                        'imageUrlBack',
                        file,
                      )
                    }
                    currentValue={vehicle.images.imageUrlBack}
                    error={!!imagesErrors?.imageUrlBack}
                    helperText={imagesErrors?.imageUrlBack as string}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ImageUploadField
                    label="Imagem Lateral Esquerda do Veículo"
                    onChange={file =>
                      handleNestedInputChange(
                        index,
                        'images',
                        'imageUrlLeft',
                        file,
                      )
                    }
                    currentValue={vehicle.images.imageUrlLeft}
                    error={!!imagesErrors?.imageUrlLeft}
                    helperText={imagesErrors?.imageUrlLeft as string}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ImageUploadField
                    label="Imagem Lateral Direita do Veículo"
                    onChange={file =>
                      handleNestedInputChange(
                        index,
                        'images',
                        'imageUrlRight',
                        file,
                      )
                    }
                    currentValue={vehicle.images.imageUrlRight}
                    error={!!imagesErrors?.imageUrlRight}
                    helperText={imagesErrors?.imageUrlRight as string}
                  />
                </Grid>
              </Grid>
            );
          })}
          <br />
          <Grid container className={classes.formActions}>
            <ButtonComponent
              theme="outline-primary"
              flex={1}
              onClick={decideToCancelOperation}
            >
              Cancelar
            </ButtonComponent>
            <ButtonComponent
              type="submit"
              theme="primary"
              flex={1}
              disabled={!isFormValid || loading}
            >
              {loading ? 'Salvando...' : 'Salvar novo veículo'}
            </ButtonComponent>
          </Grid>
        </form>
      </Box>

      <ModalCustom
        open={openCancelModal}
        onCancelClick={() => setOpenCancelModal(false)}
        onClickPublish={cancelOperation}
        ButtonOutlinePrimary="Cancelar"
        ButtonPrimary="Confirmar"
        alignCenter={true}
        hideCloseIcon={true}
        label="Deseja descartar os veículos que estavam sendo cadastrados nesse formulário?"
        boxStyle={{
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'center',
          gap: 2,
        }}
      />
    </Box>
  );
};

export default CreateVehiclesForm;